// DevRunner — boot a Munos deployment straight from the editor's buffers.
//
// This is the whole point of the dev IDE: a Play button that runs the source
// you're editing with ZERO network. It reuses the exact same runtime the real
// player uses — the WASM compiler, a MunosScriptHost, and an in-page LocalHub
// (the singleplayer local server that runs the deployment's SERVER halves over
// a loopback) — but feeds the deployment's member sources from memory instead
// of fetching them from a server URL. The member's role (client/server) is
// intrinsic to its own `client {}` / `server {}` blocks, so a one-file
// singleplayer overlay and a three-member ghost deployment boot the same way:
// each member is one tenant, all sharing one core, one bus, one local server.
//
// Mirrors the single-tile boot recipe in main.ts's `Session.start`, minus the
// couch-grid / P2P / devtools machinery a dev harness doesn't need.

import { MunosScriptHost, type MunosScriptHostOptions } from "../src/munos-script-host.js";
import { LocalHub } from "../src/local-hub.js";
import { CanvasDisplay } from "../src/canvas-display.js";
import { AudioMixer, type AudioChannel } from "../src/audio-output.js";
import { InputHub } from "../src/input.js";
import { ensureProfiles } from "../src/bindings.js";
import { findCore, inferCoreIdFromName, DEFAULT_CORE_ID, makeSynthCore } from "../src/cores/registry.js";
import { overscanForSystem } from "@multinostalgia/munos";
import type { MunosCompiler } from "@multinostalgia/munos";
import type { ChipId } from "@multinostalgia/core-contract";
import type { DestinationResolver, DeploymentResolver } from "../src/script-tenant.js";

/** One deployment member: a `.munos` source and its identity label (basename). */
export interface DevMember {
  label: string;
  source: string;
}

/** A ROM the runner boots the deployment against. */
export interface LoadedRom {
  data: Uint8Array;
  hash: string;
  name: string;
}

export type DevStatus = (kind: "info" | "error" | "connected", msg: string) => void;

// A synthetic origin for the running project. The resolvers below ignore the
// real URL and serve the in-memory members, but tenants still get a stable
// origin to scope their client-side persistence against (docs/persistence.md).
const PROJECT_URL = "dev://project";
const DEFAULT_VOLUME = 0.7;

function basename(url: string): string {
  const tail = url.split(/[/?#]/).filter(Boolean).pop() ?? url;
  return tail.replace(/\.munos$/, "");
}

export class DevRunner {
  private readonly mixer = new AudioMixer();
  private readonly inputHub = new InputHub();
  private display: CanvasDisplay | null = null;
  private channel: AudioChannel | null = null;
  private host: MunosScriptHost | null = null;
  private hub: LocalHub | null = null;
  private running = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly compiler: MunosCompiler,
    private readonly onStatus: DevStatus,
  ) {
    // One local player; sets up the default keyboard profile (WASD/arrows).
    ensureProfiles(1);
    this.inputHub.start();
  }

  isRunning(): boolean {
    return this.running;
  }

  /**
   * Tear down any running deployment and boot `members` against `rom`. Must be
   * called from a user gesture (the Play click) so the AudioContext can resume.
   */
  async play(members: DevMember[], rom: LoadedRom): Promise<void> {
    this.stop();
    if (members.length === 0) throw new Error("nothing to run: the deployment has no members");

    const coreId = inferCoreIdFromName(rom.name) ?? DEFAULT_CORE_ID;
    // Play is a user gesture — resume the audio graph (a no-op if already live).
    await this.mixer.resume();

    if (!this.display) this.display = new CanvasDisplay(this.canvas);
    if (!this.channel) this.channel = this.mixer.createChannel(DEFAULT_VOLUME);
    const sampleRate = this.channel.getSampleRate();

    const core = findCore(coreId).create({ emulateSound: true, sampleRate });
    // Size the canvas to the core's native resolution + overscan crop, so a
    // 160x144 GB frame doesn't render into a corner of a 256x240 NES canvas.
    this.display.setSize(core.screenWidth, core.screenHeight, overscanForSystem(core.system));

    // Serve the in-memory members instead of fetching a URL. A `spawn_local`
    // hop resolves a single member by label; a group hop returns them all.
    const byLabel = new Map(members.map((m) => [m.label, m] as const));
    const resolveDestination: DestinationResolver = (url) => {
      const m = byLabel.get(basename(url)) ?? members[0];
      return Promise.resolve(m ? { source: m.source, label: m.label } : null);
    };
    const resolveDeployment: DeploymentResolver = () =>
      Promise.resolve(members.map((m) => ({ source: m.source, label: m.label })));

    // A fresh local server per Play, running this deployment's server halves.
    const hub = new LocalHub({ maxClients: 1, compiler: this.compiler });
    this.hub = hub;

    // Stand the in-page server up and host EVERY member's server half on it
    // before any client half connects — the singleplayer recipe (mirrors the
    // host's spawn_local path). The client halves below ride this loopback
    // (localBus: true), so the hub must already be `ready` when they attach;
    // otherwise attachToBus falls back to the WebSocket factory and tries to
    // open a real socket to a synthetic URL. Server-side scope is the bare
    // label, matching the real server (docs/persistence.md). A member with an
    // empty server block becomes an inert member — harmless.
    await hub.ensureServer();
    for (const m of members) hub.addMember(m.source, m.label, m.label);

    const hostOpts: MunosScriptHostOptions = {
      core,
      rom: rom.data,
      // localBus: these client halves ride the in-page LocalServer's loopback
      // (LOCAL_BUS_KEY), not a bus keyed by their own URL — pure in-page play,
      // zero network. PROJECT_URL still scopes their persistence.
      tenants: members.map((m) => ({ source: m.source, url: PROJECT_URL, scriptLabel: m.label, localBus: true })),
      onFrame: (fb: Uint32Array) => this.display!.render(fb),
      onAudio: (samples: Float32Array) => this.channel!.push(samples),
      makeSynthCore: (chip: ChipId) => makeSynthCore(chip, sampleRate),
      audioSampleRate: sampleRate,
      onStatus: (kind, msg) => this.onStatus(kind, msg),
      romHash: rom.hash,
      resolveDestination,
      resolveDeployment,
      localHub: hub,
      compiler: this.compiler,
    };

    const host = new MunosScriptHost(hostOpts);
    this.host = host;
    await host.start();

    // Route keyboard input to the running host (player 1).
    this.inputHub.setHandler(0, (btn, down) => {
      if (down) host.buttonDown(btn);
      else host.buttonUp(btn);
    });
    this.running = true;
  }

  /** Stop the running deployment and free the local server. Idempotent. */
  stop(): void {
    if (this.host) {
      this.inputHub.removeHandler(0);
      this.host.stop();
      this.host = null;
    }
    if (this.hub) {
      this.hub.reset();
      this.hub = null;
    }
    this.running = false;
  }
}
